import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/Button";
import { Card, CardContent } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { ReviewSubmissionContent } from "../../components/teacher/ReviewSubmissionContent";
import {
  getSubmission,
  type SubmissionDTO,
} from "../../modules/learning/api/assignments";
import { ROUTES } from "../../utils/constants";
import { showErrorToast } from "../../utils/errorHandler";

/** Passed via `navigate(..., { state })` when opening the review page from list UIs. */
export type SubmissionReviewNavState = {
  from?: "dashboard" | "assignments";
  seedSubmission?: Partial<SubmissionDTO>;
  /** When returning after publish, Assignments page applies this tab once. */
  restoreAssignmentsView?: "recent";
};

function mergeSubmissionFromSeed(
  seed: Partial<SubmissionDTO> | undefined,
  fetched: SubmissionDTO
): SubmissionDTO {
  const seedName = seed?.student_display_name?.trim();
  const fetchedName = fetched.student_display_name?.trim();
  const seedTitle = seed?.assignment_title?.trim();
  const fetchedTitle = fetched.assignment_title?.trim();
  return {
    ...fetched,
    student_display_name: fetchedName || seedName || fetched.student_display_name,
    assignment_title: fetchedTitle || seedTitle || fetched.assignment_title,
  };
}

export const ReviewSubmissionPage: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState<SubmissionDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const from = ((location.state ?? {}) as SubmissionReviewNavState).from ?? "assignments";

  const goBack = useCallback(() => {
    if (from === "dashboard") {
      navigate(ROUTES.TEACHER_DASHBOARD);
    } else {
      navigate(ROUTES.TEACHER_ASSIGNMENTS);
    }
  }, [from, navigate]);

  const handlePublished = useCallback(() => {
    const st = (location.state ?? {}) as SubmissionReviewNavState;
    if (st.from === "assignments") {
      navigate(ROUTES.TEACHER_ASSIGNMENTS, {
        replace: true,
        state: st.restoreAssignmentsView
          ? { restoreAssignmentsView: st.restoreAssignmentsView }
          : undefined,
      });
    } else {
      navigate(ROUTES.TEACHER_DASHBOARD, { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!submissionId?.trim()) {
      setLoading(false);
      setSubmission(null);
      setError("Missing submission id.");
      return;
    }

    const seed = ((location.state ?? {}) as SubmissionReviewNavState).seedSubmission;

    const id = submissionId.trim();

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const fetched = await getSubmission(id);
        if (cancelled) return;
        setSubmission(mergeSubmissionFromSeed(seed, fetched));
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load submission.";
        setError(msg);
        setSubmission(null);
        showErrorToast(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [submissionId, location.key, location.state]);

  const backLabel = from === "dashboard" ? "Back to dashboard" : "Back to submissions";

  return (
    <Layout>
      <PageHeader
        title="Review submission"
        subtitle={
          submission
            ? submission.assignment_title || submission.assignment_id
            : "Load a submission to review and publish feedback."
        }
        actions={
          <Button type="button" variant="outline" size="sm" onClick={goBack} className="inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4 shrink-0" />
            {backLabel}
          </Button>
        }
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-24">
          <Spinner />
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading submission…</p>
        </div>
      ) : error || !submission ? (
        <Card>
          <CardContent className="py-10 text-center space-y-4">
            <p className="text-slate-700 dark:text-slate-300">
              {error || "This submission could not be loaded."}
            </p>
            <Button type="button" variant="outline" onClick={goBack}>
              {backLabel}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ReviewSubmissionContent submission={submission} onPublished={handlePublished} />
      )}
    </Layout>
  );
};
